import { queryOptions, useQuery } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import { getWebRequest } from '@tanstack/react-start/server';

import { urls } from '@repo/app-constants';
import { DOMAINS, IMPORTANCE, SECTIONS } from '@repo/monitoring';
import { getImpersonationCookie } from '@repo/utils/impersonation';

import { getAdminJWTCookie } from '@repo/utils/adminAuth';
import { API } from '~/api';
import { getJWTToken, getTokenUser } from '~/utils/jwt';

export type User = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  role: string;
  roles: string[];
  challenge_name: string;
  status: string;
  practitioner_code?: string;
};
export type JWT = string | null;
export type AuthResponse = {
  user: User;
  token: JWT;
};

export const getUserInfo = createServerFn({ method: 'GET' }).handler(
  async () => {
    console.log('[getUserInfo] Called at:', new Date().toISOString());

    // In a server context, we need to pass the cookie header to getJWTToken
    const request = getWebRequest();
    const serverCookieContext = { request };
    const token = getJWTToken(serverCookieContext);
    const user = getTokenUser(token);
    const impersonationId = getImpersonationCookie(serverCookieContext);
    const adminToken = getAdminJWTCookie(serverCookieContext);

    // Only consider the practitioner portal as "impersonating" when BOTH:
    // 1. X-Impersonate-Id cookie is set (impersonationId exists)
    // 2. th_admin_jwt cookie is set (adminToken exists)
    // This distinguishes admin-to-practitioner impersonation from practitioner-to-customer viewing.
    // When a practitioner views a customer's results portal, X-Impersonate-Id is set
    // but th_admin_jwt is not - in that case, the practitioner portal should NOT
    // change its own behavior.
    const isImpersonating = !!(impersonationId && adminToken);

    const result = {
      token,
      user,
      isImpersonating,
      impersonationId: isImpersonating ? impersonationId : undefined,
    };

    console.log('[getUserInfo] Result:', {
      hasToken: !!token,
      hasUser: !!user,
      userId: user?.id,
      isImpersonating,
      impersonationId: isImpersonating ? impersonationId : undefined,
      hasAdminToken: !!adminToken,
    });

    return result;
  },
);

const getFreshUserInfo = createServerFn({ method: 'GET' }).handler(async () => {
  try {
    const response = await API.get(
      `${urls.apiBasePath}/mainaccounts/find`,
      {},
      true,
      undefined,
      {
        tags: {
          section: SECTIONS.AUTH,
          domain: DOMAINS.ACCOUNT,
          importance: IMPORTANCE.CRITICAL,
        },
      },
    );
    return response?.result;
  } catch (error) {
    console.error('[getFreshUserInfo] Error fetching fresh user data:', error);
    throw error;
  }
});

export const userInfoQueryOptions = (enabled: boolean = true) =>
  queryOptions({
    queryKey: ['userInfo'],
    queryFn: () => getFreshUserInfo(),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });

// Custom hook to access user info and check for practitioner role
export const useUserInfo = (enableFreshData: boolean = false) => {
  const { user: contextUser } = useRouteContext({ from: '__root__' });

  // Use React Query for fresh data when needed
  const { data: freshUser } = useQuery({
    ...userInfoQueryOptions(enableFreshData && !!contextUser),
    enabled: enableFreshData && !!contextUser,
  });

  // Use fresh data if available and requested, otherwise use context user
  const user = enableFreshData && freshUser ? freshUser : contextUser;

  const isPractitioner =
    user?.roles?.some((role: string) =>
      ['practitioner', 'admin', 'impersonate'].includes(role),
    ) ?? false;

  return { user, isPractitioner };
};
